import { useEffect, useRef, useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import {
    SearchRegular,
    WhiteboardRegular,
    PeopleCommunityRegular,
    BookContactsRegular,
    ArrowSwapRegular,
    PollRegular,
    PersonRegular,
    SettingsRegular,
    BuildingRegular,
    DocumentBulletListRegular,
    TextBulletListLtrRegular,
    ArrowExitRegular,
    ArrowEnterRegular,
    DismissRegular,
    ChevronDownRegular,
} from '@fluentui/react-icons'
import { useAuth } from '../../hooks/useAuth'
import { useConfig } from '../../contexts/ConfigContext'
import styles from './NavMenu.module.css'
import { enabledAdminSections } from '../../pages/admin/adminSections'

interface SlideMenuProps {
    isOpen: boolean
    onClose: () => void
    communityhubname?: string
    isLoadingName: boolean
    setSettingsModalOpen: React.Dispatch<React.SetStateAction<boolean>>
}

const FOCUSABLE =
    'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'

export default function NavMenu({
    isOpen,
    onClose,
    communityhubname,
    isLoadingName,
    setSettingsModalOpen,
}: SlideMenuProps) {
    const navigate = useNavigate()
    const { pathname } = useLocation()
    const { user, signOut, isAnonymous, isAuthenticated } = useAuth()
    const config = useConfig()
    const {
        ask,
        contribute,
        communityHub,
        engage,
        captures,
        profilepage,
        anonymousPageAccess,
    } = config
    const adminSections = enabledAdminSections(config)
    const inAdmin = pathname === '/admin' || pathname.startsWith('/admin/')
    const [adminOpen, setAdminOpen] = useState(inAdmin)

    // Opening the menu from inside admin shows where you are.
    useEffect(() => {
        if (isOpen && inAdmin) setAdminOpen(true)
    }, [isOpen, inAdmin])

    const canAccess = (key: keyof typeof anonymousPageAccess) =>
        !isAnonymous || anonymousPageAccess[key]
    const roles = user?.roles ?? []
    const isAdmin = roles.includes('admin') || roles.includes('super_admin')

    const panelRef = useRef<HTMLDivElement>(null)
    const closeButtonRef = useRef<HTMLButtonElement>(null)
    const previousFocusRef = useRef<HTMLElement | null>(null)

    // Lock scroll and manage focus when open/closed
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden'
            previousFocusRef.current = document.activeElement as HTMLElement
            closeButtonRef.current?.focus()
        } else {
            document.body.style.overflow = ''
            previousFocusRef.current?.focus()
            previousFocusRef.current = null
        }
        return () => {
            document.body.style.overflow = ''
        }
    }, [isOpen])

    const getFocusable = () =>
        panelRef.current
            ? Array.from(
                  panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)
              )
            : []

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            onClose()
            return
        }
        if (e.key === 'Tab') {
            const focusable = getFocusable()
            if (!focusable.length) return
            const first = focusable[0]
            const last = focusable[focusable.length - 1]
            if (e.shiftKey) {
                if (document.activeElement === first) {
                    e.preventDefault()
                    last.focus()
                }
            } else {
                if (document.activeElement === last) {
                    e.preventDefault()
                    first.focus()
                }
            }
        }
    }

    const handleNav = (path: string) => {
        navigate(path)
        onClose()
    }

    const handleSignOut = async () => {
        await signOut()
        onClose()
        navigate('/login')
    }

    const handleSwitchWorkspaces = () => {
        // The /workspaces page itself drives the actual switch (via
        // switchTenant / switchTenantAnonymous). Just navigate.
        navigate('/workspaces')
        onClose()
    }

    return (
        <>
            <div
                className={`${styles.backdrop} ${isOpen ? styles.backdropVisible : ''} blur-bg`}
                onClick={onClose}
                aria-hidden="true"
            />

            <div
                id="nav-menu"
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-label="Navigation menu"
                aria-hidden={!isOpen}
                className={`${styles.panel} ${isOpen ? styles.panelOpen : ''}`}
                onKeyDown={handleKeyDown}
            >
                <div className={styles.topSection}>
                    <div className={styles.panelHeader}>
                        <div className={styles.logoArea}>
                            <p className={styles.brandName}>
                                {isLoadingName ? (
                                    <span
                                        className="skeleton"
                                        style={{
                                            display: 'inline-block',
                                            width: 120,
                                            height: 20,
                                        }}
                                    />
                                ) : (
                                    communityhubname
                                )}
                            </p>
                        </div>
                        <button
                            ref={closeButtonRef}
                            className={styles.closeButton}
                            onClick={onClose}
                            aria-label="Close navigation menu"
                        >
                            <DismissRegular
                                aria-hidden="true"
                                style={{ fontSize: 20 }}
                            />
                        </button>
                    </div>

                    <nav aria-label="Site navigation" className={styles.nav}>
                        {ask && canAccess('chat') && (
                            <NavLink
                                to="/"
                                end
                                className={({ isActive }) =>
                                    `${styles.navItem} ${isActive ? styles.navItemActive : ''}`
                                }
                                onClick={onClose}
                            >
                                <SearchRegular
                                    aria-hidden="true"
                                    style={{ fontSize: 22 }}
                                />
                                <span>Ask</span>
                            </NavLink>
                        )}

                        {contribute && canAccess('contribute') && (
                            <NavLink
                                to="/contribute"
                                className={({ isActive }) =>
                                    `${styles.navItem} ${isActive ? styles.navItemActive : ''}`
                                }
                                onClick={onClose}
                            >
                                <WhiteboardRegular
                                    aria-hidden="true"
                                    style={{ fontSize: 22 }}
                                />
                                <span>Contribute</span>
                            </NavLink>
                        )}

                        {engage && canAccess('engage') && (
                            <NavLink
                                to="/engage"
                                className={({ isActive }) =>
                                    `${styles.navItem} ${isActive ? styles.navItemActive : ''}`
                                }
                                onClick={onClose}
                            >
                                <PollRegular
                                    aria-hidden="true"
                                    style={{ fontSize: 22 }}
                                />
                                <span>Engage</span>
                            </NavLink>
                        )}

                        {communityHub && canAccess('community') && (
                            <>
                                <hr className={styles.divider} />

                                <NavLink
                                    to="/community"
                                    className={({ isActive }) =>
                                        `${styles.navItem} ${isActive ? styles.navItemActive : ''}`
                                    }
                                    onClick={onClose}
                                >
                                    <PeopleCommunityRegular
                                        aria-hidden="true"
                                        style={{ fontSize: 22 }}
                                    />
                                    <span>Community Hub</span>
                                </NavLink>

                                <NavLink
                                    to="/members"
                                    className={({ isActive }) =>
                                        `${styles.navItem} ${isActive ? styles.navItemActive : ''}`
                                    }
                                    onClick={onClose}
                                >
                                    <BookContactsRegular
                                        aria-hidden="true"
                                        style={{ fontSize: 22 }}
                                    />
                                    <span>Community Directory</span>
                                </NavLink>

                                {canAccess('exchange') && (
                                    <NavLink
                                        to="/exchange"
                                        className={({ isActive }) =>
                                            `${styles.navItem} ${isActive ? styles.navItemActive : ''}`
                                        }
                                        onClick={onClose}
                                    >
                                        <ArrowSwapRegular
                                            aria-hidden="true"
                                            style={{ fontSize: 22 }}
                                        />
                                        <span>Knowledge Exchange</span>
                                    </NavLink>
                                )}
                            </>
                        )}

                        <hr className={styles.divider} />

                        {profilepage && isAuthenticated && !isAnonymous && (
                            <NavLink
                                to="/profile"
                                className={({ isActive }) =>
                                    `${styles.navItem} ${isActive ? styles.navItemActive : ''}`
                                }
                                onClick={onClose}
                            >
                                <PersonRegular
                                    aria-hidden="true"
                                    style={{ fontSize: 22 }}
                                />
                                <span>Profile</span>
                            </NavLink>
                        )}

                        {isAuthenticated && (
                            <button
                                className={`${styles.navItem} ${styles.navButton}`}
                                onClick={() => setSettingsModalOpen(true)}
                            >
                                <SettingsRegular
                                    aria-hidden="true"
                                    style={{ fontSize: 22 }}
                                />
                                <span>Account Settings</span>
                            </button>
                        )}

                        {captures && !isAnonymous && (
                            <NavLink
                                to="/captures"
                                end
                                className={({ isActive }) =>
                                    `${styles.navItem} ${isActive ? styles.navItemActive : ''}`
                                }
                                onClick={onClose}
                            >
                                <DocumentBulletListRegular
                                    aria-hidden="true"
                                    style={{ fontSize: 22 }}
                                />
                                <span>Captures</span>
                            </NavLink>
                        )}

                        {captures && !isAnonymous && (
                            <NavLink
                                to="/captures/log"
                                className={({ isActive }) =>
                                    `${styles.navItem} ${isActive ? styles.navItemActive : ''}`
                                }
                                onClick={onClose}
                            >
                                <TextBulletListLtrRegular
                                    aria-hidden="true"
                                    style={{ fontSize: 22 }}
                                />
                                <span>My Captures</span>
                            </NavLink>
                        )}

                        {isAdmin && (
                            <div>
                                <div className={styles.accordionRow}>
                                    <NavLink
                                        to="/admin"
                                        end
                                        className={({ isActive }) =>
                                            `${styles.navItem} ${styles.accordionLink} ${isActive ? styles.navItemActive : ''}`
                                        }
                                        onClick={onClose}
                                    >
                                        <BuildingRegular
                                            aria-hidden="true"
                                            style={{ fontSize: 22 }}
                                        />
                                        <span>Admin</span>
                                    </NavLink>
                                    {adminSections.length > 0 && (
                                        <button
                                            type="button"
                                            className={`${styles.accordionToggle} ${adminOpen ? styles.accordionToggleOpen : ''}`}
                                            onClick={() =>
                                                setAdminOpen((open) => !open)
                                            }
                                            aria-expanded={adminOpen}
                                            aria-controls="nav-admin-sections"
                                            aria-label="Show admin sections"
                                        >
                                            <ChevronDownRegular
                                                aria-hidden="true"
                                                style={{ fontSize: 20 }}
                                            />
                                        </button>
                                    )}
                                </div>
                                {adminOpen && adminSections.length > 0 && (
                                    <div
                                        id="nav-admin-sections"
                                        className={styles.subList}
                                    >
                                        {adminSections.map((section) => (
                                            <NavLink
                                                key={section.id}
                                                to={section.path}
                                                className={({ isActive }) =>
                                                    `${styles.subItem} ${isActive ? styles.subItemActive : ''}`
                                                }
                                                onClick={onClose}
                                            >
                                                {section.label}
                                            </NavLink>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </nav>
                </div>

                <div className={styles.bottomSection}>
                    {(isAuthenticated || isAnonymous) && (
                        <button
                            className={`${styles.navItem} ${styles.navButton}`}
                            onClick={handleSwitchWorkspaces}
                        >
                            <ArrowSwapRegular
                                aria-hidden="true"
                                style={{ fontSize: 22 }}
                            />
                            <span>Switch Vault</span>
                        </button>
                    )}

                    {isAuthenticated && !isAnonymous ? (
                        <button
                            className={`${styles.navItem} ${styles.navButton}`}
                            onClick={handleSignOut}
                        >
                            <ArrowExitRegular
                                aria-hidden="true"
                                style={{ fontSize: 22 }}
                            />
                            <div className={styles.signOutContent}>
                                <span>Sign Out</span>
                                {user?.email && (
                                    <span className={styles.userEmail}>
                                        {user.email}
                                    </span>
                                )}
                            </div>
                        </button>
                    ) : (
                        <button
                            className={`${styles.navItem} ${styles.navButton}`}
                            onClick={() => handleNav('/login')}
                        >
                            <ArrowEnterRegular
                                aria-hidden="true"
                                style={{ fontSize: 22 }}
                            />
                            <span>Sign In</span>
                        </button>
                    )}
                </div>
            </div>
        </>
    )
}
